// pages/enter-station/enter-station.component.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { db } from "../../firebase/firebase.utils";
import { setPrjPhases } from "../../redux/prjphases/prjphases.actions";
import { setPrjTasks } from "../../redux/prjtasks/prjtasks.actions";
import { setPrjNotes } from "../../redux/prjnotes/prjnotes.actions";
import { setModules, setActiveModules } from "../../redux/space/space.actions";
import { setSpaceId, setStationId } from "../../redux/history/history.actions";
import {
  statusFilterFunction,
  timeFilterFunction,
  personFilterFunction,
} from "../../utils/taskFilters";
import { useCollabProjects } from "../../components/collab-projects/CollabProjectsProvider";
import { useSessionData } from "../../components/session-data/SessionDataProvider";
import { EMPTY_PROJECT } from "../../utils/projectCache";
import { subscribeFiles } from "../../utils/storageFiles";
import { setFiles } from "../../redux/files/files.reducer";
import { getPermissions } from "../../utils/permissions";
import Board from "../board/board.component";
import List from "../list/list.component";
import Calendar from "../calendar/calendar.component";
import GanttView from "../gantt/gantt.component";
import ConceptView from "../concept/concept.component";
import Files from "../files/Files";
import Notes from "../notes/notes";
import ChessMain from "../chess/chess.main.component";
import StationMenu from "../../components/create-station/station-menu/station-menu.component";
import TaskDetail from "../../components/task-detail/TaskDetail";
import NoteDetail from "../../components/note-detail/note-detail";
import "./enter-station.styles.scss";

const VIEWS = [
  ["b", Board],
  ["l", List],
  ["c", Calendar],
  ["g", GanttView],
  ["f", Files],
  ["n", Notes],
];
export default function EnterStation() {
  const { pathname } = useLocation();
  const [, , spaceId, , stationId] = pathname.split("/");
  const uid = useSelector((s) => s.user.currentUser?.uid);
  return (
    <ProjectContent
      key={JSON.stringify([uid, spaceId, stationId])}
      spaceId={spaceId}
      stationId={stationId}
      uid={uid}
    />
  );
}

const ProjectContent = React.memo(function ProjectContent({
  spaceId,
  stationId,
  uid,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  const conceptActive = pathname.split("/")[5] === "m";
  const [conceptVisited, setConceptVisited] = useState(conceptActive);
  useEffect(() => {
    if (conceptActive) setConceptVisited(true);
  }, [conceptActive]);
  const filter = useSelector((s) => s.filter);
  const { spaces, workspacesReady } = useCollabProjects();
  const { projectCache } = useSessionData();
  const stationData = useSelector((state) => state.space.stationData) || [];
  const cachedStation = stationData.find(
    (item) =>
      item.stationId === stationId &&
      (item.spaceId || item.fromSpaceId) === spaceId,
  );
  const [station, setStation] = useState(cachedStation || null);
  const [ready, setReady] = useState(Boolean(cachedStation));
  const [published, setPublished] = useState(false);
  const [error, setError] = useState("");
  const workspace = spaces.find((s) => s.spaceId === spaceId);
  const allowed = Boolean(
    uid && station && (workspace || station.members?.includes(uid)),
  );
  const canViewWorkspaceFiles = getPermissions(
    workspace && { ...workspace, spaceId },
    station && { ...station, fromSpaceId: spaceId },
    uid,
  ).canViewWorkspace;

  const subscribe = useCallback(
    (notify) =>
      allowed ? projectCache.subscribe(spaceId, stationId, notify) : () => {},
    [projectCache, allowed, spaceId, stationId],
  );
  const getSnapshot = useCallback(
    () =>
      allowed ? projectCache.getSnapshot(spaceId, stationId) : EMPTY_PROJECT,
    [projectCache, allowed, spaceId, stationId],
  );
  const content = useSyncExternalStore(subscribe, getSnapshot);
  const { tasks: stationTasks, notes: stationNotes, phases, modules } = content;
  const activeModules = useMemo(
    () => modules.filter((module) => module.active === true),
    [modules],
  );

  // Publish only the active project. Background snapshots update the cache alone.
  useLayoutEffect(() => {
    dispatch(setPrjTasks(stationTasks));
    dispatch(setPrjNotes(stationNotes));
    dispatch(setPrjPhases(phases));
    dispatch(setModules(modules));
    dispatch(setActiveModules(activeModules));
    setPublished(true);
  }, [dispatch, stationTasks, stationNotes, phases, modules, activeModules]);

  useEffect(() => {
    dispatch(setSpaceId(spaceId));
    dispatch(setStationId(stationId));
    if (!uid || !spaceId || !stationId) {
      setError("Invalid project address.");
      setReady(true);
      return;
    }
    let active = true;
    const stop = db
      .collection("space")
      .doc(spaceId)
      .collection("stations")
      .doc(stationId)
      .onSnapshot(
        (snap) => {
          if (!active) return;
          setStation(
            snap.exists
              ? {
                  ...snap.data(),
                  id: snap.id,
                  stationId: snap.id,
                  spaceId,
                  fromSpaceId: spaceId,
                }
              : null,
          );
          setReady(true);
          setError(snap.exists ? "" : "This project is no longer available.");
          if (!snap.exists) projectCache.forget(spaceId, stationId);
        },
        () => {
          if (!active) return;
          projectCache.forget(spaceId, stationId);
          setStation(null);
          setReady(true);
          setError("This project is unavailable or you no longer have access.");
        },
      );
    return () => {
      active = false;
      stop();
    };
  }, [uid, spaceId, stationId, dispatch, projectCache]);

  useEffect(() => {
    if (ready && workspacesReady && !allowed)
      projectCache.forget(spaceId, stationId);
  }, [ready, workspacesReady, allowed, projectCache, spaceId, stationId]);

  useEffect(() => {
    if (!allowed) {
      dispatch(
        setFiles({
          key: JSON.stringify([uid, spaceId, stationId]),
          reset: true,
          projectLoading: false,
        }),
      );
      return;
    }
    return subscribeFiles({
      spaceId,
      stationId,
      userId: uid,
      canViewWorkspace: canViewWorkspaceFiles,
      dispatch,
    });
  }, [uid, allowed, spaceId, stationId, canViewWorkspaceFiles, dispatch]);

  const stationTasksFL = useMemo(
    () =>
      stationTasks.filter(
        (task) =>
          statusFilterFunction(task, filter) !== false &&
          timeFilterFunction(task, filter) !== false &&
          personFilterFunction(task, filter) !== false,
      ),
    [stationTasks, filter],
  );

  if (!published || !ready || (!error && !allowed && !workspacesReady))
    return (
      <div className="enterStation" role="status">
        Loading project…
      </div>
    );
  if (error || !allowed)
    return (
      <div className="enterStation" role="status">
        {error || "You do not have access to this project."}
      </div>
    );
  const props = {
    station,
    stationTasks,
    stationTasksFL,
    stationNotes,
    workspace,
  };
  const detailRoutes = (
    <>
      <Route
        path="task/:taskId"
        element={<TaskDetail {...props} mode="edit" />}
      />
      <Route path="addtask" element={<TaskDetail {...props} mode="add" />} />
      <Route
        path="note/:noteId"
        element={<NoteDetail {...props} mode="edit" />}
      />
    </>
  );
  // Keep the whiteboard's own route context so relative links and details work.
  const whiteboardLocation = conceptActive
    ? location
    : {
        ...location,
        pathname: `${pathname.split("/").slice(0, 5).join("/")}/m`,
        search: "",
        hash: "",
        state: null,
      };
  return (
    <div className="enterStation">
      <StationMenu station={station} />
      {content.error && <p role="status">{content.error}</p>}
      {(conceptVisited || conceptActive) && (
        <Routes location={whiteboardLocation}>
          <Route
            path="m"
            element={<ConceptView {...props} isActive={conceptActive} />}
          >
            {detailRoutes}
          </Route>
        </Routes>
      )}
      <Routes>
        <Route index element={<Navigate to="b" replace />} />
        {VIEWS.map(([path, Component]) => (
          <Route key={path} path={path} element={<Component {...props} />}>
            {detailRoutes}
            {path === "n" && (
              <Route
                path="addnote"
                element={<NoteDetail {...props} mode="add" />}
              />
            )}
          </Route>
        ))}
        <Route path="m/*" element={null} />
        <Route path="chesstime" element={<ChessMain station={station} />} />
        <Route
          path="chesstime/task/:taskId"
          element={<TaskDetail {...props} mode="edit" />}
        />
      </Routes>
    </div>
  );
});
